import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getBills,
  createOrAddToBill,
  splitBill,
  splitAndPayBill,
  updateItemStatus,
  updateItemServed,
  applyDiscount,
  removeDiscount,
  applyItemDiscounts,
  setItemPrices,
  cancelAllBillItems,
  restoreBill,
  closeBillRetry,
  renameBill,
  transferBillWaiter,
  transferAllBillsWaiter,
  getWaiterOpenCounts,
  getFiscalIssues,
  retryStorno,
  printReceiptCopy,
} from "../api/bills";
import { cancelBillItems, type CancelBillItemsIn } from "../api/billsCancel";
import type {
  CreateBillIn,
  SplitBillIn,
  SplitAndPayBillIn,
  UpdateItemStatusIn,
  UpdateItemServedIn,
  ApplyDiscountIn,
  ApplyItemDiscountsIn,
  SetItemPricesIn,
  CancelAllItemsIn,
  RestoreBillIn,
  RetryCloseBillIn,
} from "../api/bills";
import { useAppSelector } from "../store/hooks";

export const useBills = () => {
  // Локален резерв (docs/edge.md): при включен резерв масите се опресняват
  // на 10 s И В ДВАТА РЕЖИМА — в облака всеки отговор се огледва към касата,
  // за да е списъкът ѝ пресен при отпадане на интернета; в локален режим
  // касата е единственият източник и другите терминали се виждат само така.
  const edgeEnabled = useAppSelector((state) => state.edge.enabled);
  return useQuery({
    queryKey: ["bills"],
    queryFn: getBills,
    staleTime: 0,
    refetchInterval: edgeEnabled ? 10_000 : false,
  });
};

// Проблемни фискализации — опреснява се периодично: късен резултат от вече
// отпечатан бон може да реши проблема пасивно (сметката изчезва сама).
export const useFiscalIssues = (days = 7) => {
  return useQuery({
    queryKey: ["fiscal-issues", days],
    queryFn: () => getFiscalIssues(days),
    refetchInterval: 60_000,
  });
};

export const useCreateBill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBillIn) => createOrAddToBill(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

// Сторно от Маркиране — всички избрани редове с ЕДНА заявка (един кухненски
// билет на станция); стар сървър → ред по ред (`api/billsCancel.ts`).
export const useCancelBillItems = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      billId,
      data,
    }: {
      billId: number;
      data: CancelBillItemsIn;
    }) => cancelBillItems(billId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useSplitBill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, data }: { billId: number; data: SplitBillIn }) =>
      splitBill(billId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useSplitAndPayBill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      billId,
      data,
    }: {
      billId: number;
      data: SplitAndPayBillIn;
    }) => splitAndPayBill(billId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useUpdateItemStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateItemStatusIn) => updateItemStatus(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useUpdateItemServed = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateItemServedIn) => updateItemServed(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useApplyBillDiscount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, data }: { billId: number; data: ApplyDiscountIn }) =>
      applyDiscount(billId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useRemoveBillDiscount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (billId: number) => removeDiscount(billId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useApplyItemDiscounts = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      billId,
      data,
    }: {
      billId: number;
      data: ApplyItemDiscountsIn;
    }) => applyItemDiscounts(billId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useSetItemPrices = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, data }: { billId: number; data: SetItemPricesIn }) =>
      setItemPrices(billId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

// Hook за повторно затваряне на сметка (retry) - за закрити сметки без total
export const useRenameBill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, name }: { billId: number; name: string }) =>
      renameBill(billId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useTransferBillWaiter = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      billId,
      waiterId,
      allowDuplicateName,
    }: {
      billId: number;
      waiterId: number;
      allowDuplicateName?: boolean;
    }) => transferBillWaiter(billId, waiterId, allowDuplicateName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

// Брой отворени маси по сервитьор — само докато модалът за прехвърляне е
// отворен. В локален режим касата не сервира този път (§14.6 №3 на договора):
// три напразни опита биха забавили модала, а броячите остават празни — точно
// както при провал, само че без чакане.
export const useWaiterOpenCounts = (enabled: boolean) => {
  const edgeLocal = useAppSelector((state) => state.edge.mode === "local");
  return useQuery({
    queryKey: ["bills", "waiter-open-counts"],
    queryFn: getWaiterOpenCounts,
    enabled: enabled && !edgeLocal,
    staleTime: 0,
  });
};

export const useTransferAllBillsWaiter = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      waiterId,
      allowDuplicateName,
    }: {
      waiterId: number;
      allowDuplicateName?: boolean;
    }) => transferAllBillsWaiter(waiterId, allowDuplicateName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useCloseBillRetry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      billId,
      ...data
    }: { billId: number } & RetryCloseBillIn) => closeBillRetry(billId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

// Повторно сторно (кредитно известие) за ред от storno_issues — виж
// retryStorno в api/bills.ts за request_id правилото.
export const useStornoRetry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ billId }: { billId: number }) => retryStorno(billId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-issues"] });
    },
  });
};

export const useCancelAllBillItems = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      billId,
      data,
    }: {
      billId: number;
      data: CancelAllItemsIn;
    }) => cancelAllBillItems(billId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useRestoreBill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      billId,
      data,
    }: {
      billId: number;
      data: RestoreBillIn;
    }) => restoreBill(billId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

/**
 * Печат на копие от фискалния бон на затворена сметка.
 *
 * Нищо не се инвалидира — копието не променя сметката, а `isPending` на
 * мутацията е и защитата от двойно натискане: бутонът стои disabled, докато
 * заявката е в полет, тоест втори печат не може да тръгне от бързо второ
 * кликане.
 */
export const usePrintReceiptCopy = () => {
  return useMutation({
    mutationFn: ({ billId }: { billId: number }) => printReceiptCopy(billId),
  });
};
